import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileText, Trash2, Copy, AlertCircle, CheckCircle, Loader, Download } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Upload {
  id: string;
  name: string;
  url: string;
  size: number;
  file_type: string;
  description?: string;
  created_at: string;
}

export default function AdminUploads() {
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    fetchUploads();
  }, []);

  const fetchUploads = async () => {
    try {
      setLoading(true);
      
      // Fetch uploads from database
      const { data, error } = await supabase
        .from('uploads')
        .select('*')
        .eq('deleted', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUploads(data || []);
    } catch (err) {
      console.error('Error fetching uploads:', err);
      setError('Failed to load uploads');
      setUploads([]);
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setUploading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      for (const file of acceptedFiles) {
        // Check file size
        if (file.size > 20 * 1024 * 1024) { // 20MB
          throw new Error(`File ${file.name} exceeds maximum size of 20MB`);
        }

        // Generate a unique filename but preserve the original name
        const originalName = file.name;
        const fileExt = originalName.split('.').pop();
        const timestamp = Date.now();
        const randomString = Math.random().toString(36).substring(2, 15);
        const fileName = `${timestamp}-${randomString}.${fileExt}`;
        const filePath = fileName;

        console.log(`Uploading file: ${originalName} as ${fileName}`);

        // Upload to storage
        const { error: uploadError, data: uploadData } = await supabase.storage
          .from('uploads')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          console.error('Storage upload error:', uploadError);
          throw uploadError;
        }

        console.log('File uploaded successfully:', uploadData);

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('uploads')
          .getPublicUrl(filePath);

        console.log('Public URL:', publicUrl);

        // Insert into database
        const { error: dbError, data: insertData } = await supabase
          .from('uploads')
          .insert([{
            name: originalName,
            url: publicUrl,
            size: file.size,
            file_type: file.type,
          }])
          .select();

        if (dbError) {
          console.error('Database insert error:', dbError);
          throw dbError;
        }

        console.log('Database record created:', insertData);
      }

      await fetchUploads();
      setSuccessMessage('Files uploaded successfully');
    } catch (err) {
      console.error('Error uploading files:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxSize: 20 * 1024 * 1024 // 20MB
  });

  const handleDelete = async (upload: Upload) => {
    if (!confirm('Are you sure you want to delete this file? This action cannot be undone.')) return;

    try {
      // Delete from storage
      const filePath = upload.url.split('/uploads/')[1];
      if (!filePath) throw new Error('Invalid file path');

      const { error: storageError } = await supabase.storage
        .from('uploads')
        .remove([filePath]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('uploads')
        .update({ deleted: true })
        .eq('id', upload.id);

      if (dbError) throw dbError;

      // Refresh data
      await fetchUploads();
      setSuccessMessage('File deleted successfully');
    } catch (err) {
      console.error('Error deleting file:', err);
      setError('Failed to delete file');
    }
  };

  const handleCopyUrl = (url: string, id: string) => {
    navigator.clipboard.writeText(url);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="h-12 w-12 text-[#F98B3D] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">PDF Uploads</h1>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4">
          <div className="flex">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      <div 
        {...getRootProps()} 
        className={`mb-8 border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive ? 'border-[#F98B3D] bg-orange-50' : 'border-gray-300 hover:border-[#F98B3D]'
        }`}
      >
        <input {...getInputProps()} />
        <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <p className="text-gray-600">
          {isDragActive
            ? 'Drop the PDF files here...'
            : 'Drag & drop PDF files here, or click to select files'}
        </p>
        <p className="text-sm text-gray-500 mt-2">
          Maximum file size: 20MB
        </p>
      </div>

      {uploading && (
        <div className="mb-4 bg-blue-50 border-l-4 border-blue-400 p-4">
          <div className="flex">
            <Loader className="h-5 w-5 text-blue-400 animate-spin" />
            <div className="ml-3">
              <p className="text-sm text-blue-700">Uploading files...</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {uploads.map((upload) => (
            <li key={upload.id}>
              <div className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-gray-400 mr-3" />
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {upload.name}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleCopyUrl(upload.url, upload.id)}
                      className="text-gray-400 hover:text-gray-500"
                      title="Copy URL"
                    >
                      {copied === upload.id ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <Copy className="h-5 w-5" />
                      )}
                    </button>
                    <a
                      href={upload.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-gray-500"
                      title="View"
                    >
                      <Download className="h-5 w-5" />
                    </a>
                    <button
                      onClick={() => handleDelete(upload)}
                      className="text-red-400 hover:text-red-500"
                      title="Delete"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="mt-2 sm:flex sm:justify-between">
                  <div className="sm:flex">
                    <p className="flex items-center text-sm text-gray-500">
                      {formatFileSize(upload.size)}
                    </p>
                    <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                      {upload.file_type}
                    </p>
                  </div>
                  <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                    <p>
                      Uploaded on {new Date(upload.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </li>
          ))}
          {uploads.length === 0 && (
            <li>
              <div className="px-4 py-12 text-center">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No files uploaded yet</p>
              </div>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}